import { getAnalytics, isSupported, logEvent } from 'firebase/analytics';
import { app } from '../firebase';

export const ANALYTICS_CONSENT_STORAGE_KEY = 'integra_cookie_consent';
export const ANALYTICS_CONSENT_CHANGED_EVENT = 'integra-analytics-consent-change';

let analyticsPromise = null;
let consentInitialized = false;

export function isAnalyticsDebugEnabled() {
  const value =
    process.env.REACT_APP_ANALYTICS_DEBUG ||
    process.env.NEXT_PUBLIC_ANALYTICS_DEBUG ||
    '';
  return ['1', 'true', 'yes', 'on'].includes(String(value).toLowerCase());
}

function sanitizeParams(params) {
  return Object.fromEntries(
    Object.entries(params || {}).filter(([, value]) => value !== undefined && value !== null)
  );
}

function isBrowser() {
  return typeof window !== 'undefined';
}

function applyConsentMode(consentState, mode = 'update') {
  if (!isBrowser() || typeof window.gtag !== 'function') {
    return;
  }

  const analyticsStorage = consentState === 'accepted' ? 'granted' : 'denied';
  window.gtag('consent', mode, {
    analytics_storage: analyticsStorage,
  });
}

export function getConsentState() {
  if (!isBrowser()) {
    return null;
  }

  try {
    const value = window.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY);
    if (value === 'accepted' || value === 'rejected') {
      return value;
    }
  } catch (_err) {
    // Ignore storage read failures.
  }

  return null;
}

export function canTrackAnalytics() {
  return getConsentState() === 'accepted';
}

export function initializeAnalyticsConsent() {
  if (consentInitialized) {
    return getConsentState();
  }

  const consentState = getConsentState();
  applyConsentMode(consentState, consentState ? 'update' : 'default');
  consentInitialized = true;
  return consentState;
}

export function setConsentState(nextState) {
  if (!isBrowser() || !['accepted', 'rejected'].includes(nextState)) {
    return null;
  }

  try {
    window.localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, nextState);
  } catch (_err) {
    // Ignore storage write failures.
  }

  applyConsentMode(nextState, 'update');
  consentInitialized = true;

  window.dispatchEvent(
    new CustomEvent(ANALYTICS_CONSENT_CHANGED_EVENT, {
      detail: nextState,
    })
  );

  return nextState;
}

export function subscribeToConsentChanges(listener) {
  if (!isBrowser() || typeof listener !== 'function') {
    return () => {};
  }

  const handleConsentChange = (event) => {
    listener(event?.detail || getConsentState());
  };

  window.addEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, handleConsentChange);
  return () => window.removeEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, handleConsentChange);
}

async function getAnalyticsInstance() {
  initializeAnalyticsConsent();

  if (!analyticsPromise) {
    analyticsPromise = isSupported()
      .then((supported) => (supported ? getAnalytics(app) : null))
      .catch(() => null);
  }

  const analytics = await analyticsPromise;
  applyConsentMode(getConsentState(), 'update');
  return analytics;
}

export async function trackAnalyticsEvent(eventName, params = {}, meta = {}) {
  const cleanParams = sanitizeParams(params);
  const consentState = getConsentState();

  if (isAnalyticsDebugEnabled() && typeof console !== 'undefined') {
    console.info('[analytics]', {
      event: eventName,
      params: cleanParams,
      requestId: meta.requestId,
      consentState,
    });
  }

  if (!canTrackAnalytics()) {
    return false;
  }

  const analytics = await getAnalyticsInstance();
  if (!analytics) {
    return false;
  }

  logEvent(analytics, eventName, cleanParams);
  return true;
}
